import { useState } from 'react';
import { act } from '../../lib/actions';
import { api } from '../../lib/api';
import { Button } from '../ui';
import { AddButton, Cell, ConfirmButton, Drawer, Field, inputClass, Pill, Row, SectionHeader, Table, Toggle, useLoad } from './kit';

interface Bot {
  id: string;
  name: string;
  username: string;
  queue_id: string | null;
  queue_name: string | null;
  business_hours_id: string | null;
  greeting: string;
  offline_message: string;
  active: boolean;
  last_error: string | null;
}
interface Named {
  id: string;
  name: string;
  channel?: string;
}

/**
 * Telegram bots: customers message the company's bot in Telegram, and the
 * chats come to agents like livechat. A bot is made in Telegram with
 * @BotFather, which gives its token; Nova does the rest.
 */
export function Telegram() {
  const [bots, reload] = useLoad<Bot[]>('/telegram-bots');
  const [editing, setEditing] = useState<Bot | 'new' | null>(null);
  return (
    <>
      <SectionHeader
        title="Telegram"
        lead="Customers message your Telegram bot, and the chats come to agents like livechat, under their chat limit. Make the bot in Telegram with @BotFather and paste its token here."
        action={<AddButton onClick={() => setEditing('new')}>Connect a bot</AddButton>}
      />
      <Table head={['Bot', 'Chats go to', 'Status']} empty={bots?.length === 0 && 'No bots yet. Connect one to take Telegram chats.'}>
        {(bots ?? []).map((b) => (
          <Row key={b.id} onClick={() => setEditing(b)}>
            <Cell>
              <span className="block font-medium">{b.name}</span>
              <a className="text-[12px] text-ion underline" href={`https://t.me/${b.username}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                @{b.username}
              </a>
            </Cell>
            <Cell>{b.queue_name ?? <span className="text-break">No queue: chats cannot start</span>}</Cell>
            <Cell>
              {!b.active ? <Pill>Off</Pill> : b.last_error ? <Pill tone="bad">Error</Pill> : <Pill tone="good">On</Pill>}
              {b.active && b.last_error ? <span className="mt-1 block max-w-[260px] text-[12px] text-break">{b.last_error}</span> : null}
            </Cell>
          </Row>
        ))}
      </Table>
      {editing ? (
        <BotDrawer
          bot={editing === 'new' ? null : editing}
          onDone={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

function BotDrawer({ bot, onDone }: { bot: Bot | null; onDone: () => void }) {
  const [queues] = useLoad<Named[]>('/queues');
  const [hours] = useLoad<Named[]>('/business-hours');
  const tgQueues = (queues ?? []).filter((q) => q.channel === 'telegram');
  const [f, setF] = useState({
    token: '',
    queueId: bot?.queue_id ?? '',
    businessHoursId: bot?.business_hours_id ?? '',
    greeting: bot?.greeting ?? 'Hi! Send us your question and an agent will reply here.',
    offlineMessage: bot?.offline_message ?? 'We are closed right now. We will reply when we open.',
    active: bot?.active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));

  async function save() {
    setBusy(true);
    const body = { ...f, token: f.token.trim() || undefined, businessHoursId: f.businessHoursId || null };
    const ok = await act(() => (bot ? api('PATCH', `/telegram-bots/${bot.id}`, body) : api('POST', '/telegram-bots', body)), bot ? 'Bot saved' : 'Bot connected');
    setBusy(false);
    if (ok) onDone();
  }
  async function remove() {
    if (await act(() => api('DELETE', `/telegram-bots/${bot!.id}`).then(() => true), 'Bot disconnected')) onDone();
  }

  return (
    <Drawer
      title={bot ? `${bot.name} (@${bot.username})` : 'Connect a Telegram bot'}
      open
      onClose={onDone}
      footer={
        <>
          <Button tone="primary" className="px-4 py-2" disabled={busy || !f.queueId || (!bot && !f.token.trim())} onClick={() => void save()}>
            {busy ? 'Checking with Telegram…' : bot ? 'Save' : 'Connect'}
          </Button>
          <Button tone="ghost" className="px-3 py-2" onClick={onDone}>
            Cancel
          </Button>
          {bot ? (
            <span className="ml-auto">
              <ConfirmButton confirmText="Disconnect it" onConfirm={() => void remove()}>
                Disconnect
              </ConfirmButton>
            </span>
          ) : null}
        </>
      }
    >
      {!bot ? (
        <ol className="mb-4 list-decimal space-y-1 pl-5 text-[13px] text-ink-2">
          <li>In Telegram, open @BotFather and send /newbot.</li>
          <li>Give it a name (customers see it) and a username ending in "bot".</li>
          <li>Copy the token it gives you and paste it below.</li>
        </ol>
      ) : null}
      <Field label="Bot token" hint={bot ? 'Only to replace it (for example after /revoke in @BotFather). Leave empty to keep the saved one.' : 'Like 123456789:AAE…; Nova keeps it encrypted.'}>
        {(id) => <input id={id} type="password" autoComplete="off" className={`${inputClass} tabular`} value={f.token} onChange={(e) => set('token', e.target.value)} />}
      </Field>
      <Field label="Chats go to" hint={tgQueues.length ? 'A Telegram queue. Its skills decide who gets the chats.' : 'Add a queue with channel Telegram under Queues first.'}>
        {(id) => (
          <select id={id} className={inputClass} value={f.queueId} onChange={(e) => set('queueId', e.target.value)}>
            <option value="">Choose a Telegram queue</option>
            {tgQueues.map((q) => (
              <option key={q.id} value={q.id}>
                {q.name}
              </option>
            ))}
          </select>
        )}
      </Field>
      <Field label="Open hours" hint="Outside them, customers get the closed message; their chat waits for the team. Empty: the queue's hours.">
        {(id) => (
          <select id={id} className={inputClass} value={f.businessHoursId} onChange={(e) => set('businessHoursId', e.target.value)}>
            <option value="">The queue's hours</option>
            {(hours ?? []).map((h) => (
              <option key={h.id} value={h.id}>
                {h.name}
              </option>
            ))}
          </select>
        )}
      </Field>
      <Field label="Greeting" hint="Sent when someone starts the bot. Empty: none.">
        {(id) => <textarea id={id} rows={2} className={inputClass} value={f.greeting} onChange={(e) => set('greeting', e.target.value)} />}
      </Field>
      <Field label="When closed" hint="Sent when a new chat comes in outside open hours. Empty: none.">
        {(id) => <textarea id={id} rows={2} className={inputClass} value={f.offlineMessage} onChange={(e) => set('offlineMessage', e.target.value)} />}
      </Field>
      <Toggle label="Bot on" checked={f.active} onChange={(v) => set('active', v)} />
    </Drawer>
  );
}
